import { useScrollReveal } from '@/hooks/useScrollReveal';

interface PlotSizesProps {
  plotSizes: string[];
  priceFrom: string;
}

const plotIcons: Record<string, string> = {
  '5 Marla': 'ri-layout-grid-line',
  '10 Marla': 'ri-layout-masonry-line',
  '1 Kanal': 'ri-building-3-line',
  '2 Kanal': 'ri-building-2-line',
  '1 Bed': 'ri-home-4-line',
  '2 Bed': 'ri-home-5-line',
  '3 Bed': 'ri-home-6-line',
  'Penthouse': 'ri-building-4-line',
  '4 Marla': 'ri-layout-grid-line',
  '7 Marla': 'ri-layout-masonry-line',
  '8 Marla': 'ri-layout-masonry-line',
};

const plotDescriptions: Record<string, string> = {
  '5 Marla': 'Ideal for compact families',
  '10 Marla': 'Perfect for growing families',
  '1 Kanal': 'Premium lifestyle & investment',
  '2 Kanal': 'Estate living at its finest',
  '1 Bed': 'Smart urban investment',
  '2 Bed': 'Comfortable city living',
  '3 Bed': 'Family-ready luxury',
  'Penthouse': 'Exclusive skyline living',
  '4 Marla': 'Compact commercial space',
  '7 Marla': 'Balanced residential option',
  '8 Marla': 'Spacious commercial unit',
};

const plotPrices: Record<string, string> = {
  '5 Marla': 'PKR 1,700,000',
  '10 Marla': 'PKR 3,000,000',
  '1 Kanal': 'PKR 4,200,000',
  '2 Kanal': 'PKR 17,500,000',
  '1 Bed': 'PKR 15,000,000',
  '2 Bed': 'PKR 22,000,000',
  '3 Bed': 'PKR 32,000,000',
  'Penthouse': 'PKR 55,000,000',
  '4 Marla': 'PKR 1,500,000',
  '7 Marla': 'PKR 3,800,000',
  '8 Marla': 'PKR 4,200,000',
};

export default function PlotSizes({ plotSizes, priceFrom }: PlotSizesProps) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section ref={ref} className="w-full py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-16">
        <div className={`text-center mb-12 md:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] font-bold text-[#d4853a] uppercase tracking-[0.2em] mb-3">
            Investment Options
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-[2.25rem] font-extrabold text-charcoal-300 leading-tight mb-4">
            Choose Your <span className="text-[#d4853a]">Plot Size</span>
          </h2>
          <p className="text-[15px] text-charcoal-50 max-w-xl mx-auto leading-relaxed">
            Multiple plot sizes designed to match every investment strategy and lifestyle vision.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {plotSizes.map((size, i) => (
            <div
              key={size}
              className={`group relative bg-[#FAFAF8] rounded-2xl p-6 md:p-8 border border-ivory-200 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)] hover:border-[#d4853a]/20 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: isVisible ? `${i * 120}ms` : '0ms' }}
            >
              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-[#d4853a]/5 to-transparent rounded-tr-2xl" />

              <div className="relative">
                {/* Icon */}
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#d4853a]/8 text-[#d4853a] mb-5 transition-all duration-300 group-hover:bg-[#d4853a] group-hover:text-white group-hover:shadow-lg">
                  <i className={`${plotIcons[size] || 'ri-layout-grid-line'} text-2xl`} />
                </div>

                {/* Size */}
                <h3 className="text-xl md:text-2xl font-extrabold text-charcoal-300 mb-1">{size}</h3>
                <p className="text-[13px] text-charcoal-50 mb-4">{plotDescriptions[size] || 'Premium plot option'}</p>

                {/* Price */}
                <div className="pt-4 border-t border-ivory-200">
                  <div className="text-[11px] text-charcoal-50 uppercase tracking-wider font-medium mb-1">Starting From</div>
                  <div className="text-lg font-extrabold text-[#d4853a]">
                    {plotPrices[size] || priceFrom}
                  </div>
                </div>

                {/* Availability badge */}
                <div className="mt-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#4a7c59] animate-pulse" />
                  <span className="text-[12px] font-semibold text-[#4a7c59]">Available Now</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}